import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  listDemos,
  createDemo,
  updateDemo,
  deleteDemo,
  duplicateDemo,
} from "../lib/adminApi";
import { getQuestionsGrouped } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../components/ui/sheet";
import {
  Pencil,
  Trash2,
  Copy,
  Star,
  Check,
  X,
  Search,
  ShieldCheck,
  ArrowRight,
} from "lucide-react";

const EMPTY_FORM = {
  profile_name: "",
  profile_description: "",
  scenario_type: "greenfield",
  representative_warehouse_type: "",
  complexity_label: "Medium",
  use_case: "",
  suggested_outcome: "",
  active: true,
  default: false,
  module_data: {},
};

const COMPLEXITY_OPTIONS = ["Low", "Medium", "High", "Very High"];
const SCENARIO_OPTIONS = [
  { value: "greenfield", label: "Greenfield / Pre-SAP" },
  { value: "brownfield", label: "Brownfield / SAP Upgrade" },
];

// --- Human-readable label maps (mirror the question bank + Module 2 dropdowns) ---
const INDUSTRY_LABELS = {
  manufacturing: "Manufacturing",
  retail: "Retail",
  consumer_goods: "Consumer Goods",
  pharma_life_sciences: "Pharma / Life Sciences",
  logistics_3pl: "Logistics / 3PL",
  automotive: "Automotive",
  chemicals: "Chemicals",
  other: "Other",
};
const BUSINESS_MODEL_LABELS = {
  b2b: "B2B",
  b2c: "B2C (E-commerce)",
  both: "Both",
};
const GEOGRAPHY_LABELS = {
  single_country: "Single country",
  multi_country: "Multi-country",
  global: "Global network",
};
const WH_TYPE_LABELS = {
  cdc: "Central Distribution Center",
  rdc: "Regional Distribution Center",
  manufacturing: "Manufacturing Plant Warehouse",
  ecommerce: "E-commerce Fulfillment Center",
  spare_parts: "Spare Parts / Service Warehouse",
  satellite: "Small / Satellite Warehouse",
};

const complexityStyle = (lvl) => {
  switch ((lvl || "").toLowerCase()) {
    case "low":
      return "bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]";
    case "medium":
      return "bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]";
    case "high":
      return "bg-[#FFF7ED] text-[#C2410C] border-[#FED7AA]";
    case "very high":
      return "bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]";
    default:
      return "bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]";
  }
};

export default function DemoProfilesPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saveError, setSaveError] = useState("");
  const [msg, setMsg] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [typeFilter, setTypeFilter] = useState("all");
  const [outcomeFilter, setOutcomeFilter] = useState("all");

  const load = () => {
    setLoading(true);
    listDemos()
      .then((rows) => setItems(Array.isArray(rows) ? rows : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditing(null);
    setSaveError("");
  };

  const openEdit = (p) => {
    const md = p.module_data || p.data || {};
    setForm({
      profile_name: p.profile_name || "",
      profile_description: p.profile_description || "",
      scenario_type: p.scenario_type || "greenfield",
      representative_warehouse_type: p.representative_warehouse_type || "",
      complexity_label: p.complexity_label || "Medium",
      use_case: p.use_case || "",
      suggested_outcome: p.suggested_outcome || "",
      active: p.active !== false,
      default: Boolean(p.default || p.is_default),
      module_data: md,
    });
    setEditing(p.id);
    setSaveError("");
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    resetForm();
  };

  const flash = (text) => {
    setMsg(text);
    setTimeout(() => setMsg(""), 2200);
  };

  const save = async () => {
    if (!form.profile_name.trim()) {
      setSaveError("Profile name is required.");
      return;
    }
    setSaveError("");
    const payload = { ...form, module_data: form.module_data || {} };
    try {
      if (editing) await updateDemo(editing, payload);
      else await createDemo(payload);
      flash(editing ? "Profile updated." : "Profile created.");
      closeForm();
      load();
    } catch {
      setSaveError("Error saving profile.");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete demo profile "${p.profile_name}"?`)) return;
    await deleteDemo(p.id);
    flash("Profile deleted.");
    load();
  };

  const duplicate = async (p) => {
    try {
      const cloned = await duplicateDemo(p.id);
      flash("Profile cloned.");
      // Reload the list, then immediately open the new clone in the edit drawer
      // so the user can rename + tweak answers right away. This is the only
      // sanctioned path for creating a demo profile.
      await load();
      if (cloned && cloned.id) {
        openEdit(cloned);
      }
    } catch {
      flash("Error cloning profile.");
    }
  };

  const profileStatus = (p) => p.status || (p.active === false ? "archived" : "active");

  const outcomeOptions = useMemo(() => {
    const set = new Set();
    items.forEach((p) => { if (p.suggested_outcome) set.add(p.suggested_outcome); });
    return Array.from(set).sort();
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items
      .filter((p) => {
        if (statusFilter !== "all" && profileStatus(p) !== statusFilter) return false;
        if (typeFilter !== "all" && (p.scenario_type || "") !== typeFilter) return false;
        if (outcomeFilter !== "all" && (p.suggested_outcome || "") !== outcomeFilter) return false;
        if (!q) return true;
        return [
          p.profile_name, p.profile_description, p.representative_warehouse_type,
          p.use_case, p.scenario_type, p.complexity_label, p.suggested_outcome, p.best_for,
        ].filter(Boolean).some((s) => String(s).toLowerCase().includes(q));
      })
      // Featured guided demos first, then alphabetical.
      .sort((a, b) => (Number(!!b.featured) - Number(!!a.featured)) || String(a.profile_name).localeCompare(String(b.profile_name)));
  }, [items, search, statusFilter, typeFilter, outcomeFilter]);

  const applyProfile = (p) => {
    // Reuse the existing prefill mechanism: land on SmartEvaluate Step 1 with
    // the profile queued so the consultant reviews, makes 1-2 live edits, then
    // continues. No separate launch engine / duplicate assessment creation.
    navigate(`/evaluate/module-1?demo_profile=${p.id}`);
  };

  return (
    <div data-testid="demo-profiles-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-1">
        <div>
          <h1
            className="text-xl font-bold text-[#111827]"
            style={{ fontFamily: "Chivo" }}
            data-testid="demo-profiles-title"
          >
            Demo Profiles
          </h1>
          <p className="text-xs text-[#6B7280] mt-1 max-w-2xl">
            Choose a guided scenario to quickly demonstrate SmartEvaluate with
            pre-filled data and a predictable outcome. Four featured demos cover
            the core stories; other profiles are archived and available under the
            Archived filter.
          </p>
        </div>
        <button
          onClick={() => navigate("/admin/demo-profiles/validation")}
          data-testid="demo-profiles-validation-link"
          className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold text-[#025979] border border-[#025979]/30 hover:bg-[#E0F2FE] rounded-sm px-3 py-2 transition-colors"
        >
          <ShieldCheck className="w-3.5 h-3.5" />
          Validation
        </button>
      </div>

      {msg && (
        <div
          className="my-3 px-3 py-2 bg-[#E0F2FE] border border-[#025979]/20 rounded-sm text-xs text-[#025979]"
          data-testid="demo-profiles-flash"
        >
          {msg}
        </div>
      )}

      {/* Access scope note — role-based, no runtime toggle */}
      <div
        className="mt-4 mb-4 rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] px-4 py-3"
        data-testid="demo-mode-scope-note"
      >
        <p className="text-xs font-semibold text-[#111827]">
          Who sees <em>Load Demo Profile</em>?
        </p>
        <p className="text-[11px] text-[#6B7280] mt-0.5 max-w-2xl">
          Strictly role-based. The button on the SmartEvaluate Step 1 page
          is visible only to users whose role is <strong>Admin</strong> or
          <strong> Demo</strong>. Consultants and logged-out / guest users
          never see it. No global toggle overrides this.
        </p>
      </div>

      {/* Filters — Status (default Active) · Type · Expected Outcome · Search */}
      <div className="mt-4 mb-4 flex items-center gap-2 flex-wrap">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[130px] h-8 rounded-sm text-xs" data-testid="demo-filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
            <SelectItem value="all">All statuses</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[150px] h-8 rounded-sm text-xs" data-testid="demo-filter-type">
            <SelectValue placeholder="Assessment type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="greenfield">Greenfield</SelectItem>
            <SelectItem value="brownfield">Brownfield</SelectItem>
            <SelectItem value="cloud_migration">Cloud Migration</SelectItem>
          </SelectContent>
        </Select>
        <Select value={outcomeFilter} onValueChange={setOutcomeFilter}>
          <SelectTrigger className="w-[190px] h-8 rounded-sm text-xs" data-testid="demo-filter-outcome">
            <SelectValue placeholder="Expected outcome" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All outcomes</SelectItem>
            {outcomeOptions.map((o) => (
              <SelectItem key={o} value={o}>{o}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#9CA3AF]" />
          <Input
            placeholder="Search profiles…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 rounded-sm text-xs h-8"
            data-testid="demo-profiles-search"
          />
        </div>
      </div>

      {/* Edit / Create drawer — slides in from the right, keeps the list in view */}
      <Sheet open={showForm} onOpenChange={(o) => { if (!o) closeForm(); }}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-2xl p-0 overflow-y-auto bg-white"
          data-testid="demo-profile-form"
        >
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#E5E7EB] space-y-1">
            <SheetTitle
              className="text-base font-semibold text-[#111827]"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              {editing ? "Edit Demo Profile" : "Create New Demo Profile"}
            </SheetTitle>
            <SheetDescription className="text-[11px] text-[#6B7280]">
              Configure the metadata and prefill data for this demo scenario.
            </SheetDescription>
          </SheetHeader>

          <div className="px-6 py-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="text-[11px] text-[#6B7280] font-medium">
                Profile Name <span className="text-red-500">*</span>
              </label>
              <Input
                value={form.profile_name}
                onChange={(e) =>
                  setForm({ ...form, profile_name: e.target.value })
                }
                placeholder="e.g. High-Volume Distribution Center"
                className="rounded-sm text-xs mt-1"
                data-testid="demo-profile-name-input"
              />
            </div>

            <div className="md:col-span-2">
              <label className="text-[11px] text-[#6B7280] font-medium">
                Description
              </label>
              <Textarea
                value={form.profile_description}
                onChange={(e) =>
                  setForm({ ...form, profile_description: e.target.value })
                }
                placeholder="Short summary shown on the profile card."
                className="rounded-sm text-xs mt-1 min-h-[60px]"
                data-testid="demo-profile-description-input"
              />
            </div>

            <div>
              <label className="text-[11px] text-[#6B7280] font-medium">
                Scenario Type
              </label>
              <Select
                value={form.scenario_type}
                onValueChange={(v) => setForm({ ...form, scenario_type: v })}
              >
                <SelectTrigger
                  className="rounded-sm text-xs mt-1 h-9"
                  data-testid="demo-profile-scenario-trigger"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SCENARIO_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-[11px] text-[#6B7280] font-medium">
                Complexity
              </label>
              <Select
                value={form.complexity_label}
                onValueChange={(v) => setForm({ ...form, complexity_label: v })}
              >
                <SelectTrigger
                  className="rounded-sm text-xs mt-1 h-9"
                  data-testid="demo-profile-complexity-trigger"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMPLEXITY_OPTIONS.map((o) => (
                    <SelectItem key={o} value={o}>
                      {o}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-[11px] text-[#6B7280] font-medium">
                Representative Warehouse Type
              </label>
              <Input
                value={form.representative_warehouse_type}
                onChange={(e) =>
                  setForm({
                    ...form,
                    representative_warehouse_type: e.target.value,
                  })
                }
                placeholder="e.g. Central Distribution Center"
                className="rounded-sm text-xs mt-1"
                data-testid="demo-profile-wh-type-input"
              />
            </div>

            <div>
              <label className="text-[11px] text-[#6B7280] font-medium">
                Suggested Outcome
              </label>
              <Input
                value={form.suggested_outcome}
                onChange={(e) =>
                  setForm({ ...form, suggested_outcome: e.target.value })
                }
                placeholder="e.g. EWM Advanced"
                className="rounded-sm text-xs mt-1"
                data-testid="demo-profile-outcome-input"
              />
            </div>

            <div className="md:col-span-2">
              <label className="text-[11px] text-[#6B7280] font-medium">
                Use Case
              </label>
              <Input
                value={form.use_case}
                onChange={(e) =>
                  setForm({ ...form, use_case: e.target.value })
                }
                placeholder="When to use this profile (demo / training / validation)"
                className="rounded-sm text-xs mt-1"
                data-testid="demo-profile-use-case-input"
              />
            </div>

            <div className="md:col-span-2 flex items-center gap-4 pt-1">
              <label className="text-xs flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) =>
                    setForm({ ...form, active: e.target.checked })
                  }
                  data-testid="demo-profile-active-checkbox"
                />
                Active
              </label>
              <label className="text-xs flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={form.default}
                  onChange={(e) =>
                    setForm({ ...form, default: e.target.checked })
                  }
                  data-testid="demo-profile-default-checkbox"
                />
                Default
              </label>
            </div>
          </div>

          {/* Prefilled Answers — structured editor, non-JSON */}
          <PrefilledAnswersEditor form={form} setForm={setForm} />

          {saveError && (
            <p
              className="text-[11px] text-red-600 mt-3"
              data-testid="demo-profile-error"
            >
              {saveError}
            </p>
          )}
          </div>

          {/* Sticky action bar at the bottom of the drawer */}
          <div className="sticky bottom-0 left-0 right-0 bg-white border-t border-[#E5E7EB] px-6 py-3 flex items-center justify-end gap-2">
            <Button
              variant="outline"
              onClick={closeForm}
              className="rounded-sm text-xs"
              data-testid="demo-profile-cancel-btn"
            >
              <X className="w-3 h-3 mr-1" /> Cancel
            </Button>
            <Button
              onClick={save}
              className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
              data-testid="demo-profile-save-btn"
            >
              <Check className="w-3 h-3 mr-1" /> Save Profile
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Profile Cards */}
      {loading ? (
        <div className="px-4 py-12 text-center text-xs text-[#9CA3AF]">
          Loading profiles…
        </div>
      ) : filtered.length === 0 ? (
        <div
          className="bg-white border border-dashed border-[#E5E7EB] rounded-sm px-4 py-12 text-center"
          data-testid="demo-profiles-empty"
        >
          <p className="text-sm text-[#6B7280]">
            {items.length === 0
              ? "No demo profiles yet. Click New Profile to create one."
              : "No profiles match your search."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((p) => {
            const md = p.module_data || p.data || {};
            const moduleCount = Object.keys(md || {}).length;
            return (
              <div
                key={p.id}
                className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm p-4 flex flex-col"
                data-testid={`demo-profile-card-${p.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-semibold text-[#111827] truncate">
                        {p.profile_name}
                      </h3>
                      {p.featured && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#E0F2FE] text-[#025979] border border-[#0093CB]/30 text-[10px]" data-testid={`demo-profile-featured-${p.id}`}>
                          <Star className="w-2.5 h-2.5" fill="currentColor" />
                          Featured
                        </span>
                      )}
                      {profileStatus(p) === "archived" && (
                        <span className="px-1.5 py-0.5 rounded-full bg-[#F3F4F6] text-[#6B7280] border border-[#E5E7EB] text-[10px]">
                          Archived
                        </span>
                      )}
                    </div>
                    {p.profile_description && (
                      <p className="text-[11px] text-[#6B7280] mt-1 line-clamp-2">
                        {p.profile_description}
                      </p>
                    )}
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-1.5">
                  {p.scenario_type && (
                    <span className="px-2 py-0.5 rounded-full border text-[10px] bg-[#F0EDF9] text-[#5847B0] border-[#DDD6FE]">
                      {p.scenario_type === "brownfield"
                        ? "Brownfield"
                        : p.scenario_type === "cloud_migration"
                        ? "Cloud Migration"
                        : "Greenfield"}
                    </span>
                  )}
                  {p.complexity_label && (
                    <span
                      className={`px-2 py-0.5 rounded-full border text-[10px] ${complexityStyle(
                        p.complexity_label
                      )}`}
                    >
                      {p.complexity_label} Complexity
                    </span>
                  )}
                  {p.representative_warehouse_type && (
                    <span className="px-2 py-0.5 rounded-full border text-[10px] bg-[#F9FAFB] text-[#374151] border-[#E5E7EB]">
                      {p.representative_warehouse_type}
                    </span>
                  )}
                </div>

                <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                  {p.suggested_outcome && (
                    <>
                      <dt className="text-[#6B7280]">Expected outcome</dt>
                      <dd className="text-[#111827] text-right truncate font-medium">
                        {p.suggested_outcome}
                      </dd>
                    </>
                  )}
                  {p.best_for && (
                    <>
                      <dt className="text-[#6B7280]">Best for</dt>
                      <dd className="text-[#111827] text-right truncate">{p.best_for}</dd>
                    </>
                  )}
                  {p.estimated_demo_time && (
                    <>
                      <dt className="text-[#6B7280]">Demo time</dt>
                      <dd className="text-[#111827] text-right">{p.estimated_demo_time}</dd>
                    </>
                  )}
                  {p.completion != null && (
                    <>
                      <dt className="text-[#6B7280]">Completion</dt>
                      <dd className="text-[#111827] text-right">{p.completion}% complete</dd>
                    </>
                  )}
                  {(p.live_demo_questions || []).length > 0 && (
                    <>
                      <dt className="text-[#6B7280]">Live questions</dt>
                      <dd className="text-[#111827] text-right">{(p.live_demo_questions || []).length} to answer</dd>
                    </>
                  )}
                </dl>

                <div className="mt-4 pt-3 border-t border-[#F3F4F6] flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => applyProfile(p)}
                      title="Start a new assessment pre-filled with this profile."
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold text-white bg-[#025979] hover:bg-[#024a63] transition-colors shadow-sm"
                      data-testid={`demo-profile-use-${p.id}`}
                    >
                      <ArrowRight className="w-3 h-3" />
                      Use Profile
                    </button>
                    <button
                      onClick={() => duplicate(p)}
                      title="Create a new demo scenario by cloning this profile and modifying it."
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm text-xs font-medium text-[#025979] border border-[#025979]/25 bg-white hover:bg-[#E0F2FE] transition-colors"
                      data-testid={`demo-profile-clone-${p.id}`}
                    >
                      <Copy className="w-3 h-3" />
                      Clone
                    </button>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => openEdit(p)}
                      title="Edit"
                      className="p-1.5 rounded-sm text-[#6B7280] hover:bg-[#F3F4F6] hover:text-[#025979]"
                      data-testid={`demo-profile-edit-${p.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => remove(p)}
                      title="Delete"
                      className="p-1.5 rounded-sm text-[#6B7280] hover:bg-[#FEF2F2] hover:text-[#B91C1C]"
                      data-testid={`demo-profile-delete-${p.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------
// PrefilledAnswersEditor — structured UI editor replacing the JSON editor.
//
// Renders ALL stored answers for a Demo Profile grouped as:
//   Step 1 — Company Profile       (plain inputs)
//   Step 2 — Warehouse Landscape   (native selects over the same option
//                                   lists the evaluate flow uses)
//   Step 3 — Process Evaluation    (dynamically fetched from the question
//                                   bank, grouped by section — dropdown
//                                   for single_select, number input for
//                                   numeric "operational scale" fields)
//
// Conditional sections follow the same rules as the Module 3 evaluate page:
//   - `production` is only shown when rep site == "manufacturing"
//   - `migration_readiness` is only shown when scenario == "brownfield"
//
// Edits flow through `setForm` so the parent's save path receives an
// up-to-date `form.module_data` — no JSON round-trip.
// -----------------------------------------------------------------------
function PrefilledAnswersEditor({ form, setForm }) {
  const [sections, setSections] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getQuestionsGrouped("module3")
      .then((d) => setSections(d?.sections || {}))
      .catch(() => setSections({}))
      .finally(() => setLoading(false));
  }, []);

  const md = form.module_data || {};
  const m1 = md.module1 || {};
  const m2 = md.module2 || {};
  const m3 = md.module3 || {};
  const whTypeKey = Object.keys(m3)[0] || null;
  const m3Answers = whTypeKey ? m3[whTypeKey] || {} : {};
  const scenario = m1.scenario_type || form.scenario_type || "greenfield";

  // Re-key module3 when the rep warehouse type changes so answers don't
  // orphan under the old key.
  const setWarehouseType = (newKey) => {
    const next = { ...(form.module_data || {}) };
    const currentM3 = next.module3 || {};
    const currentKey = Object.keys(currentM3)[0];
    const payload = currentKey ? currentM3[currentKey] || {} : {};
    next.module3 = { [newKey]: payload };
    const currentM2 = next.module2 || {};
    next.module2 = {
      ...currentM2,
      warehouse_types: [newKey],
      representative_sites: [newKey],
    };
    setForm({
      ...form,
      module_data: next,
      representative_warehouse_type: WH_TYPE_LABELS[newKey] || newKey,
    });
  };

  const setM1 = (key, value) => {
    setForm({
      ...form,
      module_data: { ...md, module1: { ...m1, [key]: value } },
    });
  };
  const setM2 = (key, value) => {
    setForm({
      ...form,
      module_data: { ...md, module2: { ...m2, [key]: value } },
    });
  };
  const setM3Answer = (field_key, value) => {
    const key = whTypeKey || "cdc";
    const nextAnswers = { ...m3Answers, [field_key]: value };
    if (value === "" || value == null) delete nextAnswers[field_key];
    setForm({
      ...form,
      module_data: { ...md, module3: { ...m3, [key]: nextAnswers } },
    });
  };

  // Completion ratio mirrors the Step 3 logic (required-only).
  const completion = useMemo(() => {
    if (!sections) return { answered: 0, total: 0 };
    let total = 0, answered = 0;
    for (const [secName, qlist] of Object.entries(sections)) {
      if (secName === "production" && whTypeKey !== "manufacturing") continue;
      if (secName === "migration_readiness" && scenario !== "brownfield") continue;
      for (const q of qlist) {
        if (q.is_required === false) continue;
        total += 1;
        const v = m3Answers[q.field_key];
        if (v !== undefined && v !== "" && v !== null) answered += 1;
      }
    }
    return { answered, total };
  }, [sections, m3Answers, whTypeKey, scenario]);

  const pct = completion.total
    ? Math.round((completion.answered / completion.total) * 100)
    : 0;

  const m1Field = (key, label, placeholder) => (
    <div key={key}>
      <label className="text-[10px] text-[#6B7280] font-medium">{label}</label>
      <Input
        value={m1[key] || ""}
        onChange={(e) => setM1(key, e.target.value)}
        placeholder={placeholder}
        className="rounded-sm text-xs mt-1 h-9"
        data-testid={`pf-m1-${key}`}
      />
    </div>
  );

  const selectField = (testid, value, onChange, options) => (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value || "")}
      className="w-full h-9 rounded-sm border border-[#D1D5DB] bg-white text-xs px-2 mt-1 focus:outline-none focus:ring-1 focus:ring-[#025979]"
      data-testid={testid}
    >
      <option value="">— not set —</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );

  const SECTION_LABELS = {
    org: "Warehouse Profile & Volumes",
    operational_scale: "Operational Scale",
    inbound: "Inbound Processes",
    outbound: "Outbound Processes",
    inventory: "Inventory & Internal Movements",
    automation: "Smart Warehousing",
    integration: "Integrations & Interfaces",
    reporting: "Reporting & KPIs",
    pain_points: "Business Priorities & Future Strategy",
    production: "Production Linkage",
    migration_readiness: "Current System & Migration Readiness",
  };

  return (
    <div
      className="mt-6 rounded-sm border border-[#E5E7EB] bg-white"
      data-testid="prefilled-answers-editor"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E7EB] bg-[#F8FAFC]">
        <div>
          <p className="text-xs font-semibold text-[#111827]">
            Prefilled Answers (Complete View)
          </p>
          <p className="text-[10px] text-[#6B7280] mt-0.5">
            All stored answers for this demo scenario — edit directly, no JSON needed.
          </p>
        </div>
        {sections && (
          <span
            className="text-[10px] font-semibold text-[#025979] bg-[#E0F2FE] border border-[#025979]/20 px-2 py-1 rounded-full"
            data-testid="prefilled-step3-pct"
          >
            Step 3: {completion.answered}/{completion.total} ({pct}%)
          </span>
        )}
      </div>

      <div className="p-4 space-y-5">
        {/* Step 1 — Company Profile */}
        <section data-testid="pf-step-1">
          <p className="text-[10px] font-semibold tracking-wider text-[#025979] uppercase mb-2">
            Step 1 — Company Profile
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
            {m1Field("company_name", "Company Name", "Acme Corp")}
            {m1Field("contact_name", "Contact Name", "Demo Client")}
            {m1Field("email", "Email", "demo@example.com")}
            {m1Field("role", "Role", "Operations Manager")}
            <div>
              <label className="text-[10px] text-[#6B7280] font-medium">
                Scenario Type
              </label>
              {selectField(
                "pf-m1-scenario",
                m1.scenario_type || form.scenario_type,
                (v) => {
                  setForm({
                    ...form,
                    scenario_type: v,
                    module_data: { ...md, module1: { ...m1, scenario_type: v } },
                  });
                },
                SCENARIO_OPTIONS,
              )}
            </div>
          </div>
        </section>

        {/* Step 2 — Warehouse Landscape */}
        <section data-testid="pf-step-2">
          <p className="text-[10px] font-semibold tracking-wider text-[#025979] uppercase mb-2">
            Step 2 — Warehouse Landscape
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
            <div>
              <label className="text-[10px] text-[#6B7280] font-medium">Industry</label>
              {selectField(
                "pf-m2-industry", m2.industry,
                (v) => setM2("industry", v),
                Object.entries(INDUSTRY_LABELS).map(([value, label]) => ({ value, label })),
              )}
            </div>
            <div>
              <label className="text-[10px] text-[#6B7280] font-medium">Business Model</label>
              {selectField(
                "pf-m2-business-model", m2.business_model,
                (v) => setM2("business_model", v),
                Object.entries(BUSINESS_MODEL_LABELS).map(([value, label]) => ({ value, label })),
              )}
            </div>
            <div>
              <label className="text-[10px] text-[#6B7280] font-medium">Warehouse Count</label>
              {selectField(
                "pf-m2-count", m2.warehouse_count,
                (v) => setM2("warehouse_count", v),
                ["1", "2-5", "6-15", "15+"].map((v) => ({ value: v, label: v })),
              )}
            </div>
            <div>
              <label className="text-[10px] text-[#6B7280] font-medium">Geography</label>
              {selectField(
                "pf-m2-geography", m2.geography,
                (v) => setM2("geography", v),
                Object.entries(GEOGRAPHY_LABELS).map(([value, label]) => ({ value, label })),
              )}
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] text-[#6B7280] font-medium">
                Representative Warehouse Type
              </label>
              {selectField(
                "pf-m2-wh-type", whTypeKey,
                (v) => v && setWarehouseType(v),
                Object.entries(WH_TYPE_LABELS).map(([value, label]) => ({ value, label })),
              )}
            </div>
          </div>
        </section>

        {/* Step 3 — Process Evaluation (dynamic from question bank) */}
        <section data-testid="pf-step-3">
          <p className="text-[10px] font-semibold tracking-wider text-[#025979] uppercase mb-2">
            Step 3 — Process Evaluation
          </p>
          {loading ? (
            <p className="text-[11px] text-[#9CA3AF]">Loading questions…</p>
          ) : !sections || Object.keys(sections).length === 0 ? (
            <p className="text-[11px] text-[#9CA3AF]">No Step 3 questions configured.</p>
          ) : (
            <div className="space-y-4">
              {Object.entries(sections).map(([secName, qlist]) => {
                if (secName === "production" && whTypeKey !== "manufacturing") return null;
                if (secName === "migration_readiness" && scenario !== "brownfield") return null;
                return (
                  <div
                    key={secName}
                    className="border border-[#F3F4F6] rounded-sm p-3 bg-[#FDFDFD]"
                    data-testid={`pf-step3-section-${secName}`}
                  >
                    <p className="text-[11px] font-semibold text-[#111827] mb-2">
                      {SECTION_LABELS[secName] || secName}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {qlist.map((q) => {
                        const fk = q.field_key;
                        if (!fk) return null;
                        const v = m3Answers[fk];
                        const atype = q.answer_type;
                        const isNumber = atype === "number" || atype === "optional_number";
                        return (
                          <div key={q.id || fk}>
                            <label
                              className="text-[10px] text-[#6B7280] font-medium block truncate"
                              title={q.question_text}
                            >
                              {q.question_text}
                            </label>
                            {isNumber ? (
                              <Input
                                type="number"
                                value={v ?? ""}
                                onChange={(e) =>
                                  setM3Answer(fk, e.target.value === "" ? "" : Number(e.target.value))
                                }
                                placeholder={q.placeholder || "—"}
                                className="rounded-sm text-xs mt-1 h-8"
                                data-testid={`pf-q-${fk}`}
                              />
                            ) : (
                              selectField(
                                `pf-q-${fk}`,
                                v,
                                (nv) => setM3Answer(fk, nv),
                                (q.answer_options || []).map((o) => ({
                                  value: o.value,
                                  label: o.label,
                                })),
                              )
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
